// src/common.ts
import { SESSION_CODE_LENGTH, SESSION_CODE_REGEX } from "@lume/shared";
import { z } from "zod";
var cuidSchema = z.string().min(20).max(40).regex(/^c[a-z0-9]+$/i, "expected a cuid");
var emailSchema = z.string().trim().toLowerCase().email("expected a valid email address");
var isoDateStringSchema = z.string().datetime({ offset: true, message: "expected an ISO-8601 date string" });
var sessionCodeSchema = z.string().trim().toUpperCase().length(SESSION_CODE_LENGTH, `session code must be ${SESSION_CODE_LENGTH} characters`).regex(SESSION_CODE_REGEX, "session code uses an invalid character");
var slugSchema = z.string().trim().toLowerCase().min(2).max(40).regex(/^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/, "invalid slug");
var apiErrorSchema = z.object({
  statusCode: z.number().int().min(400).max(599),
  error: z.string(),
  message: z.string(),
  details: z.unknown().optional(),
  timestamp: isoDateStringSchema,
  path: z.string(),
  requestId: z.string().optional()
});
var planSchema = z.enum(["FREE", "PRO", "TEAM", "ENTERPRISE"]);

// src/auth.ts
import { z as z2 } from "zod";
var requestMagicLinkRequestSchema = z2.object({
  email: emailSchema,
  redirectTo: z2.string().url().optional()
});
var requestMagicLinkResponseSchema = z2.object({
  ok: z2.literal(true),
  /** Always true so the API does not leak whether the email is registered. */
  emailSent: z2.literal(true)
});
var magicLinkCallbackRequestSchema = z2.object({
  token: z2.string().min(20)
});
var authenticatedUserSchema = z2.object({
  id: cuidSchema,
  email: emailSchema,
  name: z2.string().nullable(),
  avatarUrl: z2.string().url().nullable(),
  organizationId: cuidSchema.nullable(),
  createdAt: isoDateStringSchema
});
var authSessionSchema = z2.object({
  user: authenticatedUserSchema,
  accessToken: z2.string(),
  accessTokenExpiresAt: isoDateStringSchema
});
var accessTokenPayloadSchema = z2.object({
  sub: cuidSchema,
  email: emailSchema,
  organizationId: cuidSchema.nullable(),
  type: z2.literal("access")
});
var magicLinkTokenPayloadSchema = z2.object({
  sub: emailSchema,
  type: z2.literal("magic-link"),
  /** Random nonce so the same email cannot reuse a previous link's signature. */
  nonce: z2.string().min(8)
});
var joinTokenPayloadSchema = z2.object({
  sub: z2.string(),
  type: z2.literal("session-join"),
  sessionCode: z2.string(),
  sessionId: cuidSchema
});
var sessionHostTokenPayloadSchema = z2.object({
  sub: cuidSchema,
  type: z2.literal("session-host"),
  sessionCode: z2.string(),
  sessionId: cuidSchema,
  organizationId: cuidSchema.nullable()
});

// src/downloads.ts
import { z as z3 } from "zod";
var desktopPlatformSchema = z3.enum([
  "macos-arm64",
  "macos-intel",
  "windows-x64",
  "linux-x64"
]);
var desktopNotifyRequestSchema = z3.object({
  email: emailSchema,
  platform: desktopPlatformSchema
});
var desktopNotifyResponseSchema = z3.object({
  ok: z3.literal(true)
});

// src/organizations.ts
import { z as z4 } from "zod";
var organizationSchema = z4.object({
  id: cuidSchema,
  name: z4.string().min(1).max(120),
  slug: slugSchema,
  plan: planSchema,
  createdAt: isoDateStringSchema
});
var createOrganizationRequestSchema = z4.object({
  name: z4.string().trim().min(1).max(120),
  slug: slugSchema.optional()
});
var updateOrganizationRequestSchema = z4.object({
  name: z4.string().trim().min(1).max(120).optional(),
  slug: slugSchema.optional()
});

// src/sessions.ts
import { z as z5 } from "zod";
var sessionStatusSchema = z5.enum(["PENDING", "ACTIVE", "ENDED", "CANCELLED"]);
var sessionMetadataSchema = z5.object({
  os: z5.string().max(64).optional(),
  osVersion: z5.string().max(32).optional(),
  browser: z5.string().max(64).optional(),
  browserVersion: z5.string().max(32).optional(),
  screenWidth: z5.number().int().positive().optional(),
  screenHeight: z5.number().int().positive().optional(),
  devicePixelRatio: z5.number().positive().optional(),
  language: z5.string().max(16).optional(),
  timezone: z5.string().max(64).optional()
});
var sessionSchema = z5.object({
  id: cuidSchema,
  code: sessionCodeSchema,
  status: sessionStatusSchema,
  hostUserId: cuidSchema,
  organizationId: cuidSchema.nullable(),
  clientName: z5.string().nullable(),
  startedAt: isoDateStringSchema.nullable(),
  endedAt: isoDateStringSchema.nullable(),
  createdAt: isoDateStringSchema,
  metadata: sessionMetadataSchema.nullable()
});
var createSessionRequestSchema = z5.object({
  clientName: z5.string().trim().min(1).max(120).optional()
});
var iceServersSchema = z5.array(
  z5.object({
    urls: z5.union([z5.string(), z5.array(z5.string())]),
    username: z5.string().optional(),
    credential: z5.string().optional()
  })
);
var createSessionResponseSchema = z5.object({
  session: sessionSchema,
  /**
   * URL the technician can copy to share with the customer. The end customer
   * opens this URL in a browser to start screen sharing.
   */
  joinUrl: z5.string().url(),
  /**
   * Short-lived JWT that authorises this technician to host THIS session on
   * the signaling service. The plain access token is no longer accepted there:
   * it proves identity but not ownership of a session code.
   */
  hostToken: z5.string(),
  hostTokenExpiresAt: isoDateStringSchema,
  /** ICE servers for the technician side of the connection. */
  iceServers: iceServersSchema,
  signalingUrl: z5.string()
});
var sessionInfoResponseSchema = z5.object({
  code: sessionCodeSchema,
  status: sessionStatusSchema,
  /** Name of the technician hosting the session, displayed to the customer. */
  hostName: z5.string(),
  /** Optional organization name, shown next to the technician name. */
  organizationName: z5.string().nullable()
});
var joinSessionRequestSchema = z5.object({
  /** The customer-typed name displayed to the technician. */
  clientName: z5.string().trim().min(1).max(120).optional(),
  metadata: sessionMetadataSchema.optional()
});
var joinSessionResponseSchema = z5.object({
  session: sessionSchema,
  /**
   * Short-lived JWT the client-web app passes to the signaling service so
   * the relay can match it to a session without an account.
   */
  joinToken: z5.string(),
  joinTokenExpiresAt: isoDateStringSchema,
  /** ICE servers (STUN + TURN) the WebRTC client should configure. */
  iceServers: iceServersSchema,
  signalingUrl: z5.string()
});
var listSessionsQuerySchema = z5.object({
  status: sessionStatusSchema.optional(),
  limit: z5.coerce.number().int().min(1).max(100).default(20),
  cursor: cuidSchema.optional()
});
var listSessionsResponseSchema = z5.object({
  sessions: z5.array(sessionSchema),
  nextCursor: cuidSchema.nullable()
});

// src/signaling.ts
import { z as z6 } from "zod";
var signalingRoleSchema = z6.enum(["host", "client"]);
var signalingAuthSchema = z6.object({
  role: signalingRoleSchema,
  token: z6.string().min(10),
  sessionCode: sessionCodeSchema
});
var peerJoinedEventSchema = z6.object({
  role: signalingRoleSchema,
  socketId: z6.string(),
  sessionId: cuidSchema
});
var peerLeftEventSchema = z6.object({
  role: signalingRoleSchema,
  socketId: z6.string()
});
var signalingErrorEventSchema = z6.object({
  code: z6.enum(["INVALID_TOKEN", "INVALID_SESSION", "SESSION_FULL", "PEER_NOT_FOUND", "INTERNAL"]),
  message: z6.string()
});
var sdpDescriptionSchema = z6.object({
  type: z6.enum(["offer", "answer", "pranswer", "rollback"]),
  sdp: z6.string()
});
var sdpMessageSchema = z6.object({
  to: z6.string(),
  from: z6.string(),
  description: sdpDescriptionSchema
});
var iceCandidateSchema = z6.object({
  candidate: z6.string(),
  sdpMid: z6.string().nullable(),
  sdpMLineIndex: z6.number().int().nullable(),
  usernameFragment: z6.string().nullable().optional()
});
var iceCandidateMessageSchema = z6.object({
  to: z6.string(),
  from: z6.string(),
  candidate: iceCandidateSchema.nullable()
});
var controlEventSchema = z6.discriminatedUnion("kind", [
  z6.object({
    kind: z6.literal("cursor-move"),
    x: z6.number(),
    y: z6.number(),
    timestamp: z6.number().int()
  }),
  z6.object({
    kind: z6.literal("cursor-click"),
    x: z6.number(),
    y: z6.number(),
    button: z6.enum(["left", "middle", "right"]),
    timestamp: z6.number().int()
  }),
  z6.object({
    kind: z6.literal("key"),
    code: z6.string(),
    pressed: z6.boolean(),
    timestamp: z6.number().int()
  })
]);
var SIGNALING_EVENTS = {
  peerJoined: "peer:joined",
  peerLeft: "peer:left",
  sdp: "webrtc:sdp",
  ice: "webrtc:ice",
  control: "control:event",
  error: "lume:error"
};
export {
  SIGNALING_EVENTS,
  accessTokenPayloadSchema,
  apiErrorSchema,
  authSessionSchema,
  authenticatedUserSchema,
  controlEventSchema,
  createOrganizationRequestSchema,
  createSessionRequestSchema,
  createSessionResponseSchema,
  cuidSchema,
  desktopNotifyRequestSchema,
  desktopNotifyResponseSchema,
  desktopPlatformSchema,
  emailSchema,
  iceCandidateMessageSchema,
  iceCandidateSchema,
  iceServersSchema,
  isoDateStringSchema,
  joinSessionRequestSchema,
  joinSessionResponseSchema,
  joinTokenPayloadSchema,
  listSessionsQuerySchema,
  listSessionsResponseSchema,
  magicLinkCallbackRequestSchema,
  magicLinkTokenPayloadSchema,
  organizationSchema,
  peerJoinedEventSchema,
  peerLeftEventSchema,
  planSchema,
  requestMagicLinkRequestSchema,
  requestMagicLinkResponseSchema,
  sdpDescriptionSchema,
  sdpMessageSchema,
  sessionCodeSchema,
  sessionHostTokenPayloadSchema,
  sessionInfoResponseSchema,
  sessionMetadataSchema,
  sessionSchema,
  sessionStatusSchema,
  signalingAuthSchema,
  signalingErrorEventSchema,
  signalingRoleSchema,
  slugSchema,
  updateOrganizationRequestSchema
};
//# sourceMappingURL=index.mjs.map